/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { EdgeType, describeInput } from "./schemas.js";
import { collectPorts } from "./ports.js";
import { filterBySchema } from "../../schema.js";
/**
 * This is a special kind of an `InspectableNode`, representing a bubbled
 * node instance. Its key difference is that, when it's type = input,
 * it will narrow down the ports and values to only the ones that bubbled up.
 * For instance, the actual input node might have four parameters, but
 * only one of them bubbled up. This node will make sure that it looks as if
 * it only has that one parameter.
 */
export class BubbledInspectableNode {
    descriptor;
    #actual;
    constructor(actual) {
        const descriptor = actual.descriptor;
        if (descriptor.type !== "input" && descriptor.type !== "output") {
            throw new Error("BubbledInspectableNode can only be an input or an output");
        }
        this.#actual = actual;
        this.descriptor = descriptor;
    }
    title() {
        return this.#actual.title();
    }
    description() {
        return this.#actual.description();
    }
    incoming() {
        return this.#actual.incoming();
    }
    outgoing() {
        return this.#actual.outgoing();
    }
    isEntry() {
        return this.#actual.isEntry();
    }
    isExit() {
        return this.#actual.isExit();
    }
    type() {
        return this.#actual.type();
    }
    configuration() {
        return this.#actual.configuration();
    }
    metadata() {
        return this.#actual.metadata();
    }
    async describe(inputs) {
        if (this.descriptor.type === "input") {
            return describeInput({ inputs });
        }
        return this.#actual.describe(inputs);
    }
    #portsForInput(inputValues, outputValues) {
        const described = describeInput({ inputs: inputValues });
        const bubbledValues = filterBySchema(outputValues || {}, described.outputSchema);
        const inputs = {
            fixed: described.inputSchema.additionalProperties === false,
            ports: collectPorts(EdgeType.In, this.incoming(), described.inputSchema, false, true, inputValues),
        };
        const outputs = {
            fixed: described.outputSchema.additionalProperties === false,
            ports: collectPorts(EdgeType.Out, [], described.outputSchema, false, false, bubbledValues),
        };
        const side = {
            fixed: true,
            ports: [],
        };
        return { inputs, outputs, side, updating: false };
    }
    async ports(inputValues, outputValues) {
        if (this.descriptor.type === "input") {
            return this.#portsForInput(inputValues, outputValues);
        }
        return this.#actual.ports(inputValues, outputValues || undefined);
    }
    currentPorts(inputValues, outputValues) {
        if (this.descriptor.type === "input") {
            return this.#portsForInput(inputValues, outputValues);
        }
        return this.#actual.currentPorts(inputValues, outputValues);
    }
    deleted() {
        return this.#actual.deleted();
    }
}
//# sourceMappingURL=bubbled-node.js.map